import React, { useState } from 'react';
import { Checkbox, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import cx from 'classnames';
import type { ManagedBillingProviderVo } from 'types/insuranceBilling';
import s from './s.module.less';

type Props = {
    providers: ManagedBillingProviderVo[];
    value: number[];
    onChange: (ids: number[]) => void;
    // 当前登录 provider 的 bp id，名字后标 (You)
    selfProviderId?: number;
    // 不可勾选（已在所有计费 EHR 里关掉的 provider），行尾标 Already off
    disabledIds?: number[];
};

const initials = (name: string) =>
    name
        .split(' ')
        .filter(Boolean)
        .map((w) => w.charAt(0))
        .join('')
        .slice(0, 2)
        .toUpperCase();

// 原型 mountProvPicker：搜索 + Select all（搜索时为 Select all N shown）+ "X of N selected" + 滚动列表
export default function ProviderPicker({ providers, value, onChange, selfProviderId, disabledIds = [] }: Props) {
    const [q, setQ] = useState('');
    const k = q.trim().toLowerCase();
    const disabled = new Set(disabledIds);
    const selectable = providers.filter((p) => !disabled.has(p.id));
    const shown = providers.filter((p) => !k || p.name.toLowerCase().includes(k));
    const shownSelectable = shown.filter((p) => !disabled.has(p.id));
    const selected = new Set(value);
    const allShownOn = shownSelectable.length > 0 && shownSelectable.every((p) => selected.has(p.id));

    const toggleAll = (checked: boolean) => {
        const next = new Set(value);
        shownSelectable.forEach((p) => (checked ? next.add(p.id) : next.delete(p.id)));
        onChange(providers.filter((p) => next.has(p.id)).map((p) => p.id));
    };

    const toggle = (id: number, checked: boolean) => {
        onChange(checked ? [...value, id] : value.filter((v) => v !== id));
    };

    return (
        <div className={s.provPick}>
            <div className={s.provPickTop}>
                <Input
                    allowClear
                    prefix={<SearchOutlined className={s.provPickQIcon} />}
                    placeholder="Search providers"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
            </div>
            <div className={s.provPickBar}>
                <Checkbox
                    className={s.provPickAll}
                    checked={allShownOn}
                    disabled={!shownSelectable.length}
                    onChange={(e) => toggleAll(e.target.checked)}
                >
                    {k ? `Select all ${shownSelectable.length} shown` : 'Select all'}
                </Checkbox>
                <span>
                    {selectable.filter((p) => selected.has(p.id)).length} of {selectable.length} selected
                </span>
            </div>
            <div className={s.provPickList}>
                {shown.length ? (
                    shown.map((p) => {
                        const off = disabled.has(p.id);
                        const on = !off && selected.has(p.id);
                        return (
                            <Checkbox
                                key={p.id}
                                className={cx(s.provPickRow, on && s.provPickRowOn)}
                                checked={on}
                                disabled={off}
                                onChange={(e) => toggle(p.id, e.target.checked)}
                            >
                                <span className={s.provPickAv}>{initials(p.name)}</span>
                                <span>
                                    {p.name}
                                    {selfProviderId != null && p.bpProviderId === selfProviderId ? ' (You)' : ''}
                                </span>
                                {off && <span className={s.offTag}>Already off</span>}
                            </Checkbox>
                        );
                    })
                ) : (
                    <div className={s.provPickEmpty}>No provider matches that search.</div>
                )}
            </div>
        </div>
    );
}
